import {
  PIZZA_BASE_CATEGORY,
  T,
  formatCents,
  ingredientGroups,
  portionLabel,
  portionPriceCents,
  sizeCmFromName,
  tt,
  type BuilderIngredient,
  useModalFocus,
} from '@agos/shared'
import { cn } from '@agos/ui'
import { Pizza, X } from 'lucide-react'
import { useRef, useState } from 'react'

import {
  cycleExtra,
  extrasCents,
  extrasText,
  menuPizzaBaseOf,
  parseExtras,
  withMenuPizzaBase,
  type ChosenExtras,
} from './pizza-extras'

/**
 * Toppings on a pizza off the menu — `FRONTEND.md` §9.41, ported from legacy
 * `pizza-extras-modal.tsx`.
 *
 * Grouped by §9.25's rule rather than legacy's hard-coded `["Syr","Mäso","Zelenina"]`: that drops the
 * *Základ* rows — a pizza that already comes with a tomato base cannot take a second one as an extra —
 * and shows a category a franchise has added, which legacy's list would hide.
 *
 * **And the base itself can be changed - §9.731, owner O100**: *"nevieme ani meniť základ pizze"*. Not as a topping,
 * which is the reason above, but as one choice over the franchise's `Základ` rows with *Bez zmeny* first and chosen.
 * Free, and written as the bracket's first piece (`withMenuPizzaBase`), never into the line's `extras`. The garlic
 * crust needs nothing of its own here: it is an `Okraj` row, offered among the toppings exactly as the custom
 * builder offers it, because both draw `ingredientGroups(ingredients)` over the same list (§9.93).
 */
export function PizzaExtrasDialog({
  productName,
  unitPrice,
  ingredients,
  initialExtras,
  onClose,
  onConfirm,
}: {
  productName: string
  unitPrice: number | string
  ingredients: readonly BuilderIngredient[]
  initialExtras?: string
  onClose: () => void
  /**
   * §10A.80 — the chosen ids travel with the text and the price. The server prices them; the price
   * here is what the screen quotes, and the two agree because both read `ingredientForSize`.
   */
  onConfirm: (
    extras: string,
    extrasPriceCents: number,
    chosen: ReadonlyArray<{ ingredientId: number; quantity: number }>,
  ) => void
}) {
  // The size is read off the product's name, which is where it lives — *Margherita 50 cm*. Legacy
  // does the same and falls back to 32, the size a pizza is when nobody said otherwise.
  const sizeCm = sizeCmFromName(productName) ?? 32
  // §9.93 — an `Okraj` row is an ordinary topping now, so `ingredientGroups` returns it with the
  // rest and there is nothing separate to draw or to price.
  const groups = ingredientGroups(ingredients)
  const offered = groups.flatMap((group) => group.ingredients)
  const [chosen, setChosen] = useState<ChosenExtras>(() => parseExtras(initialExtras, offered))

  // §9.731 - the bases on offer, and the one this line already carries. A stored base that is no longer offered stays
  // a choice, because dropping it here would take it off the line on the next save with nothing on screen to say so.
  const storedBase = menuPizzaBaseOf(initialExtras)
  const offeredBases = (
    ingredientGroups(ingredients, true).find((group) => group.category === PIZZA_BASE_CATEGORY)?.ingredients ?? []
  ).map((ingredient) => ingredient.name.trim())
  const baseChoices =
    storedBase === null || offeredBases.some((name) => sameBase(name, storedBase))
      ? offeredBases
      : [...offeredBases, storedBase]
  const [base, setBase] = useState<string | null>(storedBase)
  const lineText = withMenuPizzaBase(extrasText(chosen, offered), base)

  const surcharge = extrasCents(chosen, offered, sizeCm)

  /* §9.485, `TODO.md` 3c - the trap and the Escape together, on the `<section>` rather than the wrapper:
     the wrapper holds the dismiss scrim as a sibling of this card, and §9.469's reasoning is that a role
     there would put the dismiss control inside the dialog it dismisses. `true` because this renders `null`
     while closed, and the close is unconditional - the dialog collects a selection and makes no request of
     its own, so there is no moment when dismissing it loses a write. */
  const panelRef = useRef<HTMLElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, onClose)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-pizza-extras>
      <button
        type="button"
        className="absolute inset-0 bg-black/60"
        aria-label={tt('Zavrieť')}
        onClick={onClose}
      />
      <section
        className="relative w-full max-w-md max-h-[85vh] flex flex-col rounded-2xl bg-background border border-border overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pizza-extras-title"
        ref={panelRef}
        onKeyDown={onKeyDown}
      >
        <header className="px-5 pt-4 pb-3 border-b border-border shrink-0 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <Pizza className="w-4 h-4 text-primary" />
              <T>Prísady navyše</T>
            </p>
            <h2 className="text-base font-bold leading-tight truncate" id="pizza-extras-title">{productName}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {sizeCm} cm · {formatCents(surcharge)}
            </p>
          </div>
          <button
            type="button"
            data-dialog-cancel
            aria-label={tt('Zavrieť')}
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted shrink-0"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {baseChoices.length > 0 && (
            <div data-base-choice>
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <T>Základ</T>
              </p>
              <p className="text-[11px] text-muted-foreground mb-2">
                <T>Zmena základu je bez príplatku.</T>
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                {[null, ...baseChoices].map((name) => {
                  const picked = name === null ? base === null : base !== null && sameBase(base, name)
                  return (
                    <button
                      key={name ?? ''}
                      type="button"
                      data-base-option={name ?? ''}
                      aria-pressed={picked}
                      onClick={() => setBase(name)}
                      className={cn(
                        'rounded-xl border p-2 text-xs text-left transition-colors',
                        picked
                          ? 'border-primary bg-primary/10 font-semibold'
                          : 'border-border bg-card hover:border-primary/50',
                      )}
                    >
                      {name === null ? <T>Bez zmeny</T> : name}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
          {groups.map((group) => (
            <div key={group.category}>
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                <T>{group.category}</T>
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                {group.ingredients.map((ingredient) => {
                  const qty = chosen.get(ingredient.id)
                  return (
                    <button
                      key={ingredient.id}
                      type="button"
                      data-extra={ingredient.id}
                      data-extra-qty={qty ?? 0}
                      // §9.703 - each tap a portion, up to the builder's three, and the next one removes it.
                      onClick={() => setChosen((current) => cycleExtra(current, ingredient.id))}
                      className={cn(
                        'flex items-center justify-between gap-2 rounded-xl border p-2 text-xs text-left transition-colors',
                        /* §9.678's one colour for every chosen count - the count below is what tells them apart. This
                         * dialog still drew a double in orange, the colour the builders gave up. */
                        qty === undefined
                          ? 'border-border bg-card hover:border-primary/50'
                          : 'border-primary bg-primary/10 font-semibold',
                      )}
                    >
                      <span className="flex items-center gap-1.5 min-w-0">
                        {qty !== undefined && (
                          <span className="shrink-0 text-sm font-extrabold text-primary" data-extra-portions>
                            {portionLabel(qty)}
                          </span>
                        )}
                        <span className="truncate">{ingredient.name}</span>
                      </span>
                      <span className="shrink-0 tabular-nums text-[10px] text-muted-foreground">
                        {formatCents(portionPriceCents(ingredient, sizeCm, qty ?? 0))}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        <footer className="p-4 border-t border-border shrink-0 space-y-2">
          <p className="text-xs text-muted-foreground" data-extras-summary>
            {lineText === '' ? <T>Bez prísad navyše</T> : lineText}
          </p>
          <button
            type="button"
            data-extras-save
            onClick={() =>
              onConfirm(
                // §9.731 - the base first, then the toppings; the base never goes into the chosen ids below.
                lineText,
                surcharge,
                [...chosen].map(([ingredientId, quantity]) => ({ ingredientId, quantity })),
              )
            }
            className="w-full min-h-[48px] rounded-xl bg-primary text-primary-foreground font-bold hover:opacity-90 transition-opacity"
          >
            <T>Uložiť</T> · {formatCents(Math.round(Number(unitPrice) * 100) + surcharge)}
          </button>
        </footer>
      </section>
    </div>
  )
}

/** Two base names as the dialog compares them - case and spaces folded, the way the server matches a name. */
function sameBase(left: string, right: string): boolean {
  const fold = (name: string) => name.toLowerCase().trim().replace(/\s+/g, ' ')
  return fold(left) === fold(right)
}
